import clsx from 'clsx';
import type { JSX } from 'react';
import type { ShowingSlotAdmin } from '@/lib/services/showings';
import {
  formatDayLabel,
  formatSlotLabel,
} from '@/components/dashboard/showings/showingsAvailabilityWeek';

interface ShowingsAvailabilityWeekTableProps {
  weekDates: string[];
  timeRows: string[];
  slotMaps: Record<string, Map<string, ShowingSlotAdmin>>;
  isUpdating: boolean;
  onToggle: (date: string, time: string, shiftKey: boolean) => void;
  onOpenBooking: (showingId: string) => void;
}

export function ShowingsAvailabilityWeekTable({
  weekDates,
  timeRows,
  slotMaps,
  isUpdating,
  onToggle,
  onOpenBooking,
}: ShowingsAvailabilityWeekTableProps): JSX.Element {
  const dayLabels = new Map<string, string>();
  weekDates.forEach((date) => {
    dayLabels.set(date, formatDayLabel(date));
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[52rem] border-separate border-spacing-2">
        <thead>
          <tr>
            <th className="px-2 text-left text-xs font-semibold uppercase tracking-wide text-text-primary/60">
              Time
            </th>
            {weekDates.map((date) => (
              <th
                key={date}
                className="px-2 text-left text-xs font-semibold uppercase tracking-wide text-text-primary/60"
              >
                {dayLabels.get(date) ?? date}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {timeRows.map((time) => (
            <tr key={time}>
              <th className="whitespace-nowrap px-2 text-left text-sm font-semibold text-text-primary">
                {formatSlotLabel(time)}
              </th>
              {weekDates.map((date) => {
                const slot = slotMaps[date].get(time) ?? null;
                const status = slot?.status ?? 'blocked';
                const bookedShowingId =
                  status === 'booked' &&
                  typeof slot?.showingId === 'string' &&
                  slot.showingId.length > 0
                    ? slot.showingId
                    : null;
                const canOpenBooking = bookedShowingId !== null;
                const statusLabel =
                  status === 'available' ? 'Available' : status === 'booked' ? 'Booked' : 'Blocked';
                const dayLabel = dayLabels.get(date) ?? date;
                const label = `${dayLabel} ${formatSlotLabel(time)} (${status})`;
                const titleParts = [`${dayLabel} at ${formatSlotLabel(time)}`, statusLabel];

                if (typeof slot?.note === 'string' && slot.note.length > 0) {
                  titleParts.push(slot.note);
                }

                if (canOpenBooking) {
                  titleParts.push('Click to open');
                }

                return (
                  <td key={`${date}-${time}`} className="px-2">
                    <button
                      type="button"
                      aria-label={label}
                      title={titleParts.join(' - ')}
                      disabled={isUpdating && status !== 'booked'}
                      className={clsx(
                        'min-h-11 w-full rounded-xl border px-2 py-2 text-xs font-semibold uppercase tracking-wide shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:cursor-not-allowed disabled:opacity-70',
                        status === 'available'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
                          : status === 'booked'
                            ? 'border-amber-200 bg-amber-50 text-amber-900'
                            : 'border-surface-subtle bg-surface-subtle/40 text-text-primary/70 hover:bg-surface-subtle/60',
                      )}
                      onClick={(event) => {
                        if (status === 'booked') {
                          if (bookedShowingId !== null) {
                            onOpenBooking(bookedShowingId);
                          }
                          return;
                        }

                        onToggle(date, time, event.shiftKey);
                      }}
                    >
                      {statusLabel}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
